import { type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

type Size = 'sm' | 'md' | 'lg';

export function Button({
  variant = 'secondary',
  size = 'md',
  loading,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: Size; loading?: boolean }): JSX.Element {
  return (
    <button className={`tbm-btn tbm-btn--${variant} tbm-btn--${size} ${className}`} disabled={rest.disabled || loading} {...rest}>
      {loading ? '…' : children}
    </button>
  );
}

export function IconButton(props: ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
  const { className = '', ...rest } = props;
  return <Button className={`tbm-icon-btn ${className}`} {...rest} />;
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="tbm-field">
      <span className="tbm-field__label">
        {label}
        {required ? <span className="tbm-field__req">*</span> : null}
      </span>
      {children}
      {hint ? <span className="muted">{hint}</span> : null}
      {error ? <span className="tbm-error">{error}</span> : null}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input className="tbm-input" {...props} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea className="tbm-textarea" {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return <select className="tbm-select" {...props} />;
}

export function Checkbox({
  state = 'unchecked',
  label,
  onToggle,
}: {
  state?: 'checked' | 'unchecked' | 'indeterminate';
  label?: string;
  onToggle?: () => void;
}): JSX.Element {
  return (
    <span className="tbm-check">
      <input
        type="checkbox"
        checked={state === 'checked'}
        ref={(el) => {
          if (el) el.indeterminate = state === 'indeterminate';
        }}
        onChange={() => onToggle?.()}
      />
      {label}
    </span>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }): JSX.Element {
  return (
    <label className="tbm-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Rechercher' }: { value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  return (
    <Input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      aria-label={placeholder}
    />
  );
}
